import { SEOData } from "../components/seo/seo-context";

/**
 * Creates SEO data for a specific entity (like an IDO, project, etc.)
 *
 * @param entity The data entity to create SEO for
 * @param options Additional options for SEO generation
 * @returns SEO data object
 */
export const createEntitySEO = <T extends Record<string, any>>(
  entity: T | undefined | null,
  options: {
    titlePrefix?: string;
    titleSuffix?: string;
    nameField?: keyof T;
    descriptionField?: keyof T;
    imageField?: keyof T;
    fallbackTitle?: string;
    fallbackDescription?: string;
    fallbackImage?: string;
    isArticle?: boolean;
  } = {}
): SEOData => {
  const {
    titlePrefix = "",
    titleSuffix = " | AlphaMind",
    nameField = "name" as keyof T,
    descriptionField = "description" as keyof T,
    imageField = "image" as keyof T,
    fallbackTitle = "AlphaMind",
    fallbackDescription = "AlphaMind is a decentralized fundraising platform for blockchain projects.",
    fallbackImage = "https://app.alphamind.co/logo.png",
    isArticle = false,
  } = options;

  if (!entity) {
    return {
      title: fallbackTitle,
      description: fallbackDescription,
      image: fallbackImage,
      article: isArticle,
    };
  }

  const name = entity[nameField] as string | undefined;
  const description = entity[descriptionField] as string | undefined;
  const image = entity[imageField] as string | undefined;

  return {
    title: name ? `${titlePrefix}${name}${titleSuffix}` : fallbackTitle,
    description: description || fallbackDescription,
    image: image || fallbackImage,
    article: isArticle,
  };
};

/**
 * Creates SEO data for a specific route
 *
 * @param title The page title
 * @param description The page description
 * @param options Additional SEO options
 * @returns SEO data object
 */
export const createRouteSEO = (
  title: string,
  description: string,
  options: {
    titleSuffix?: string;
    image?: string;
    isArticle?: boolean;
  } = {}
): SEOData => {
  const {
    titleSuffix = " | AlphaMind",
    image = "https://app.alphamind.co/logo.png",
    isArticle = false,
  } = options;

  return {
    title: `${title}${titleSuffix}`,
    description,
    image,
    article: isArticle,
  };
};
